import type { TIncomingCall } from "../types/callCenter.types";
import type {
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../services/types/user.types";

type TMainUserSlice = {
  id: number | string;
  companyId?: number;
};

/**
 * Prefer a live main-page inquiry row matching the MVoice caller; otherwise the same synthetic
 * document shape MainPage uses when opening the drawer after hang-up (CDR delivered).
 */
export function resolveInquiryDocumentForIncomingCall(
  call: TIncomingCall,
  therapist: TMainUserSlice,
  therapistWithUsers: TTherapistWithUsers[] | null | undefined,
  usersWithOutTherapist: TUsersWithOutTherapist[] | null | undefined
): TTherapistWithUsers | TUsersWithOutTherapist {
  if (call.isIdentified && call.callerInfo) {
    const guid = (call.callerInfo.guid ?? "").trim();
    const uid = call.callerInfo.userId;

    const fromMine =
      therapistWithUsers?.find(
        (d) => (guid.length > 0 && d.guid === guid) || d.usersId === uid
      ) ?? null;

    const fromPool =
      !fromMine
        ? usersWithOutTherapist?.find(
            (d) => (guid.length > 0 && d.guid === guid) || d.usersId === uid
          )
        : null;

    if (fromMine || fromPool) {
      return (fromMine ?? fromPool)!;
    }

    const ci = call.callerInfo;
    return {
      num: ci.referralNumber
        ? parseInt(ci.referralNumber.replace("#", ""), 10)
        : 0,
      questionnaireCompaniesId: ci.questionnaireCompaniesId ?? 1,
      cycle: ci.cycle ?? ci.currentCycle ?? 1,
      timestamp: new Date().toISOString(),
      questionnaireCompaniesType: "אינטייק",
      usersId: ci.userId,
      companyId: therapist?.companyId || 1,
      fakeCompanyId: null,
      fakeCompanyName: null,
      firstName: ci.firstName,
      lastName: ci.lastName,
      fullName: `${ci.firstName} ${ci.lastName || ""}`,
      usersName: `${ci.firstName} ${ci.lastName || ""}`,
      phone: ci.phone || "",
      email: null,
      userQuestionnaireId: ci.questionnaireId ?? 1,
      therapistQuestionnaireId: 1,
      generalRRMSEQ: null,
      specificFields: "[]",
      status: 1,
      comments: null,
      birth: null,
      guid: ci.guid,
      therapistCycle: null,
      appointmentsId: null,
      appointmentsTypeId: null,
      appointmentsActive: null,
      therapistActionId: null,
      therapistsUsersId: null,
      therapistsId: Number(therapist?.id) || 0,
      files: "",
      questionnaireCompaniesTypeId: 1,
    } satisfies TTherapistWithUsers;
  }

  return {
    num: 0,
    questionnaireCompaniesId: 1,
    cycle: 1,
    timestamp: new Date().toISOString(),
    questionnaireCompaniesType: "פנייה חדשה",
    usersId: 0,
    companyId: therapist?.companyId || 1,
    fakeCompanyId: null,
    fakeCompanyName: null,
    firstName: "מתקשר",
    lastName: "לא מזוהה",
    fullName: "מתקשר לא מזוהה",
    usersName: "מתקשר לא מזוהה",
    phone: call.cli,
    email: null,
    userQuestionnaireId: 0,
    therapistQuestionnaireId: 0,
    generalRRMSEQ: null,
    specificFields: "[]",
    status: 1,
    comments: null,
    birth: null,
    guid: "",
    therapistCycle: null,
    appointmentsId: null,
    appointmentsTypeId: null,
    appointmentsActive: null,
    therapistActionId: null,
    therapistsUsersId: null,
    questionnaireCompaniesTypeId: 1,
  };
}
